import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Stack, Typography, Button, Chip, IconButton, Paper,
  List, ListItemButton, ListItemIcon, ListItemText, Collapse,
  Tooltip, Skeleton, alpha, useTheme, Divider, Link as MuiLink
} from '@mui/material';
import {
  ExpandMore, ExpandLess, Star, ContentCopy, AddCircleOutline,
  Close, MenuBook, Visibility
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { Link as RouterLink } from 'react-router-dom';

import { SessionClass, SectionInstance, CurriculumSubject } from '../../types/sessions';
import { Subject } from '../../types';
import { classesApi } from '../../services/sessions/classesApi';
import { sectionInstancesApi } from '../../services/sessions/sectionInstancesApi';
import { curriculumApi } from '../../services/sessions/curriculumApi';
import { subjectService } from '../../services/subjectService';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { useSubscription } from '../../context/SubscriptionContext';

import AssignSubjectsDialog from './AssignSubjectsDialog';
import CopyCurriculumDialog from './CopyCurriculumDialog';
import EffectiveSubjectsPanel from './EffectiveSubjectsPanel';
import ConfirmationDialog from '../common/ConfirmationDialog';
import EmptyState from '../common/EmptyState';

interface CurriculumTabProps {
  sessionId: string;
}

// A node identifies which (class, sectionInstance?) slot the right pane is showing.
// sectionInstanceId === null   → common subjects node ("★" under the class)
// sectionInstanceId === string → per-section node
interface SelectedNode {
  classId: string;
  className: string;
  sectionInstanceId: string | null;
  sectionName: string | null;
}

const CurriculumTab: React.FC<CurriculumTabProps> = ({ sessionId }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { canWrite } = useModulePermissions();
  const { hasFeature } = useSubscription();
  const writable = canWrite('curriculum');
  // Periods per week and the other timetable rules live on the curriculum
  // entry but are edited in Timetable → Setup → Lessons; this tab only points there.
  const hasTimetable = hasFeature('timetable');

  const [classes, setClasses] = useState<SessionClass[]>([]);
  const [sections, setSections] = useState<SectionInstance[]>([]);
  const [rows, setRows] = useState<CurriculumSubject[]>([]);
  const [subjectCatalog, setSubjectCatalog] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(true);

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [selected, setSelected] = useState<SelectedNode | null>(null);

  const [assignOpen, setAssignOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [effectiveOpen, setEffectiveOpen] = useState(false);
  const [effectiveCtx, setEffectiveCtx] = useState<{ classId: string; className: string; sectionInstanceId: string; sectionName: string } | null>(null);
  const [removeTarget, setRemoveTarget] = useState<CurriculumSubject | null>(null);
  const [removing, setRemoving] = useState(false);

  const subjectLookup = useMemo(
    () => new Map(subjectCatalog.map(s => [String(s._id), s])),
    [subjectCatalog]
  );

  const sectionsByClass = useMemo(() => {
    const map = new Map<string, SectionInstance[]>();
    for (const s of sections) {
      const k = String(s.classId);
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(s);
    }
    return map;
  }, [sections]);

  const rowsByKey = useMemo(() => {
    // key: `classId::sectionInstanceId|null` → array of curriculum rows
    const map = new Map<string, CurriculumSubject[]>();
    for (const r of rows) {
      const k = `${String(r.classId)}::${r.sectionInstanceId ? String(r.sectionInstanceId) : 'null'}`;
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(r);
    }
    return map;
  }, [rows]);

  // Initialize-once guard. Without it, the load callback's closure captures
  // an empty `expanded` and resets the user's selection on every reload.
  const initializedRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cls, secs, cur, cat] = await Promise.all([
        classesApi.list(),
        sectionInstancesApi.list({ sessionId }),
        curriculumApi.list({ sessionId }),
        subjectService.getSubjects({ isActive: true, limit: 500 })
      ]);
      setClasses(cls);
      setSections(secs);
      setRows(cur);
      setSubjectCatalog(cat.subjects);
      // Set default expansion + selection ONCE — never on subsequent reloads.
      if (!initializedRef.current && cls.length) {
        initializedRef.current = true;
        setExpanded({ [String(cls[0]._id)]: true });
        setSelected({
          classId: String(cls[0]._id),
          className: cls[0].name,
          sectionInstanceId: null,
          sectionName: null
        });
      }
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not load curriculum', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [sessionId, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  // Optimistic update helpers — no full reload after assign / remove so the
  // user's selected node and expand state stay stable.
  const removeRow = useCallback((id: string) => {
    setRows(prev => prev.filter(r => r._id !== id));
  }, []);
  // Refetches ONLY curriculum rows — classes/sections/subjectCatalog stay
  // cached, and the user's selected node + expanded map don't get reset.
  const reloadRows = useCallback(async () => {
    try {
      const cur = await curriculumApi.list({ sessionId });
      setRows(cur);
    } catch { /* surface only via outer toast */ }
  }, [sessionId]);

  const toggle = (classId: string) => setExpanded(prev => ({ ...prev, [classId]: !prev[classId] }));

  const handleSelect = (node: SelectedNode) => {
    setSelected(node);
    setExpanded(prev => ({ ...prev, [node.classId]: true }));
  };

  const requestRemove = (row: CurriculumSubject) => setRemoveTarget(row);

  const confirmRemove = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await curriculumApi.remove(removeTarget._id);
      enqueueSnackbar('Subject removed', { variant: 'success' });
      removeRow(removeTarget._id);   // optimistic — no full reload, no scroll jump
      setRemoveTarget(null);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not remove subject', { variant: 'error' });
    } finally {
      setRemoving(false);
    }
  };

  const openEffectivePanel = (classId: string, className: string, section: SectionInstance) => {
    setEffectiveCtx({ classId, className, sectionInstanceId: section._id, sectionName: section.name });
    setEffectiveOpen(true);
  };

  // ── Render helpers ────────────────────────────────────────────────────
  const renderRightPane = () => {
    if (!selected) {
      return (
        <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', py: 6 }}>
          <MenuBook sx={{ fontSize: 36, color: 'text.disabled', mb: 1.5 }} />
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 280 }}>
            Pick a class or section on the left to view and assign its subjects.
          </Typography>
        </Stack>
      );
    }

    const key = `${selected.classId}::${selected.sectionInstanceId ?? 'null'}`;
    const slotRows = rowsByKey.get(key) ?? [];
    const isCommon = selected.sectionInstanceId == null;
    const slotLabel = isCommon ? 'Common subjects' : `Section: ${selected.sectionName}`;


    return (
      <Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          sx={{ mb: 2 }}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              {isCommon
                ? <Star sx={{ fontSize: 18, color: 'warning.main' }} />
                : <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />
              }
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6 }}>
                {slotLabel}
              </Typography>
            </Stack>
            <Typography variant="h6" fontWeight={700}>{selected.className}</Typography>
            <Typography variant="body2" color="text.secondary">
              {isCommon
                ? 'These subjects apply to every student in this class, regardless of section.'
                : 'These subjects apply only to students in this section, in addition to the class\'s common subjects.'}
            </Typography>
          </Box>
          {writable && (
            <Button
              variant="contained"
              size="medium"
              disableElevation
              startIcon={<AddCircleOutline />}
              onClick={() => setAssignOpen(true)}
            >
              Add subjects
            </Button>
          )}
        </Stack>

        {hasTimetable && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Periods and timetable rules are set in{' '}
            <MuiLink component={RouterLink} to="/app/timetable/setup?tab=lessons" underline="hover">Timetable → Setup → Lessons</MuiLink>.
          </Typography>
        )}

        {slotRows.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 4, borderRadius: 2, borderStyle: 'dashed', textAlign: 'center' }}>
            <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 500 }}>
              {isCommon ? 'No common subjects yet' : 'No subjects assigned to this section yet'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isCommon
                ? 'Add subjects that every student in this class takes (e.g., English, Urdu).'
                : 'Add subjects unique to this section (e.g., Physics for Science, Accounting for Commerce).'}
            </Typography>
          </Paper>
        ) : (
          <Paper variant="outlined" sx={{ borderRadius: 2 }}>
            <List disablePadding>
              {slotRows.map((row, idx) => {
                const subject = subjectLookup.get(String(row.subjectId));
                if (!subject) return null;
                return (
                  <React.Fragment key={row._id}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 2, py: 1.25,
                        transition: 'background-color 120ms ease',
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) }
                      }}
                    >
                      {/* Subject identity */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body1" fontWeight={600} noWrap>{subject.name}</Typography>
                        {subject.description && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            sx={{ display: 'block' }}
                          >
                            {subject.description}
                          </Typography>
                        )}
                      </Box>

                      {writable && (
                        <Tooltip title="Remove from curriculum">
                          <IconButton size="small" onClick={() => requestRemove(row)}>
                            <Close fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    {idx < slotRows.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </List>
          </Paper>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Skeleton variant="rounded" width={300} height={420} sx={{ borderRadius: 2 }} />
        <Skeleton variant="rounded" sx={{ flex: 1, borderRadius: 2 }} height={420} />
      </Stack>
    );
  }

  if (classes.length === 0) {
    return (
      <EmptyState
        dense
        icon={<MenuBook />}
        title="No classes defined yet"
        description="Add classes from Administration → Classes first; curriculum is assigned per class."
      />
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        {writable && (
          <Button
            variant="text"
            size="small"
            startIcon={<ContentCopy fontSize="small" />}
            onClick={() => setCopyOpen(true)}
            sx={{ color: 'text.secondary' }}
          >
            Copy curriculum from another session
          </Button>
        )}
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        {/* Tree navigator */}
        <Paper variant="outlined" sx={{ width: { xs: '100%', md: 320 }, borderRadius: 2, alignSelf: 'flex-start', maxHeight: { md: '70vh' }, overflowY: 'auto' }}>
          <List dense disablePadding>
            {classes.map((cls, idx) => {
              const classKey = String(cls._id);
              const open = !!expanded[classKey];
              const classSections = sectionsByClass.get(classKey) ?? [];
              const commonKey = `${classKey}::null`;
              const commonCount = (rowsByKey.get(commonKey) ?? []).length;
              const isClassSelected = selected?.classId === classKey;

              return (
                <Box key={classKey}>
                  <ListItemButton onClick={() => toggle(classKey)} sx={{ py: 1.25 }}>
                    <ListItemText
                      primary={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="subtitle2" fontWeight={600}>{cls.name}</Typography>
                          {classSections.length > 0 && (
                            <Chip size="small" variant="outlined" label={classSections.length} sx={{ height: 20, fontSize: 11 }} />
                          )}
                        </Stack>
                      }
                    />
                    {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                  </ListItemButton>
                  <Collapse in={open} unmountOnExit>
                    {/* Common node */}
                    <ListItemButton
                      sx={{
                        pl: 4,
                        bgcolor: isClassSelected && selected?.sectionInstanceId == null
                          ? alpha(theme.palette.primary.main, 0.10) : 'transparent',
                        borderLeft: '2px solid',
                        borderLeftColor: isClassSelected && selected?.sectionInstanceId == null
                          ? 'primary.main' : 'transparent'
                      }}
                      onClick={() => handleSelect({
                        classId: classKey, className: cls.name, sectionInstanceId: null, sectionName: null
                      })}
                    >
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <Star sx={{ fontSize: 16, color: 'warning.main' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="body2" fontWeight={500}>Common subjects</Typography>
                            {commonCount > 0 && (
                              <Chip size="small" label={commonCount} sx={{ height: 18, fontSize: 10 }} variant="outlined" />
                            )}
                          </Stack>
                        }
                      />
                    </ListItemButton>

                    {/* Per-section nodes */}
                    {classSections.length === 0 ? (
                      <Box sx={{ pl: 4, py: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          No sections in this session — only common subjects apply.
                        </Typography>
                      </Box>
                    ) : classSections.map(section => {
                      const sectionKey = `${classKey}::${section._id}`;
                      const sectionCount = (rowsByKey.get(sectionKey) ?? []).length;
                      const isSel = isClassSelected && selected?.sectionInstanceId === section._id;
                      return (
                        <ListItemButton
                          key={section._id}
                          sx={{
                            pl: 4,
                            bgcolor: isSel ? alpha(theme.palette.primary.main, 0.10) : 'transparent',
                            borderLeft: '2px solid',
                            borderLeftColor: isSel ? 'primary.main' : 'transparent'
                          }}
                          onClick={() => handleSelect({
                            classId: classKey, className: cls.name,
                            sectionInstanceId: section._id, sectionName: section.name
                          })}
                        >
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography variant="body2" fontWeight={500}>Section {section.name}</Typography>
                                {sectionCount > 0 && (
                                  <Chip size="small" label={sectionCount} sx={{ height: 18, fontSize: 10 }} variant="outlined" />
                                )}
                              </Stack>
                            }
                            secondary={
                              <MuiLink
                                component="button"
                                variant="caption"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEffectivePanel(classKey, cls.name, section);
                                }}
                                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, mt: 0.3, color: 'text.secondary', textDecorationStyle: 'dotted' }}
                              >
                                <Visibility sx={{ fontSize: 13 }} /> View effective subjects
                              </MuiLink>
                            }
                          />
                        </ListItemButton>
                      );
                    })}
                  </Collapse>
                  {idx < classes.length - 1 && <Divider />}
                </Box>
              );
            })}
          </List>
        </Paper>

        {/* Right pane */}
        <Paper variant="outlined" sx={{ flex: 1, borderRadius: 2, p: 2.5, minHeight: 360 }}>
          {renderRightPane()}
        </Paper>
      </Stack>

      {/* Dialogs */}
      {selected && (
        <AssignSubjectsDialog
          open={assignOpen}
          sessionId={sessionId}
          classId={selected.classId}
          className={selected.className}
          sectionInstanceId={selected.sectionInstanceId}
          sectionName={selected.sectionName ?? undefined}
          alreadyAssignedIds={(rowsByKey.get(`${selected.classId}::${selected.sectionInstanceId ?? 'null'}`) ?? []).map(r => String(r.subjectId))}
          onClose={() => setAssignOpen(false)}
          onAssigned={reloadRows}
        />
      )}
      <CopyCurriculumDialog
        open={copyOpen}
        targetSessionId={sessionId}
        onClose={() => setCopyOpen(false)}
        onCopied={reloadRows}
      />
      {effectiveCtx && (
        <EffectiveSubjectsPanel
          open={effectiveOpen}
          sessionId={sessionId}
          classId={effectiveCtx.classId}
          className={effectiveCtx.className}
          sectionInstanceId={effectiveCtx.sectionInstanceId}
          sectionName={effectiveCtx.sectionName}
          onClose={() => setEffectiveOpen(false)}
        />
      )}

      <ConfirmationDialog
        open={Boolean(removeTarget)}
        title="Remove subject?"
        message={
          removeTarget
            ? `${subjectLookup.get(String(removeTarget.subjectId))?.name ?? 'This subject'} will be removed from this slot. Existing exam results that reference it stay intact; future exams will skip it.`
            : ''
        }
        confirmText="Remove"
        confirmColor="error"
        loading={removing}
        onClose={() => setRemoveTarget(null)}
        onConfirm={confirmRemove}
      />
    </Box>
  );
};

export default CurriculumTab;
